import { ConnectionHandler, type RecordSourceSelectorProxy } from 'relay-runtime';
import { fg } from '@atlassian/jira-feature-gating';
import type { JiraNavigationItemTypeKey } from '@atlassian/jira-relay/src/__generated__/viewItem_businessAddTab.graphql';

const JIRA_BUSINESS_BASE_PROJECT_URL = '/jira/core/projects';

export const getRouteForViewType = (
	projectKey: string,
	itemTypeKey: JiraNavigationItemTypeKey,
): string | undefined => {
	switch (itemTypeKey) {
		case 'SUMMARY':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/summary`;
		case 'BOARD':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/board`;
		case 'LIST':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/list`;
		case 'TIMELINE':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/timeline`;
		case 'CALENDAR':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/calendar`;
		case 'FORMS':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/form`;
		case 'APPROVALS':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/approvals`;
		case 'ATTACHMENTS':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/attachments`;
		case 'PAGES':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/pages`;
		case 'ISSUES':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/issues`;
		case 'REPORTS':
			return fg('reports-overview-page-migration')
				? `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/reports`
				: `/projects/${projectKey}?selectedItem=com.atlassian.jira.jira-projects-plugin:report-page`;
		case 'COMPONENTS':
			return `${JIRA_BUSINESS_BASE_PROJECT_URL}/${projectKey}/components`;
		default:
			return undefined;
	}
};

export const getConnection = (
	store: RecordSourceSelectorProxy,
	cloudId: string,
	projectIdOrKey: string,
) => {
	const parentRecord = store.getRoot().getLinkedRecord('jira');
	if (parentRecord == null) {
		return undefined;
	}

	return ConnectionHandler.getConnection(parentRecord, 'tabs_jiraNavigation__navigationItems', {
		cloudId,
		projectIdOrKey,
	});
};

export const getStoreEdges = (
	store: RecordSourceSelectorProxy,
	cloudId: string,
	projectIdOrKey: string,
) => {
	const connection = getConnection(store, cloudId, projectIdOrKey);
	if (!connection) return undefined;

	return connection.getLinkedRecords('edges');
};

export const getAnalyticsTabOrder = (
	store: RecordSourceSelectorProxy,
	cloudId: string,
	projectIdOrKey: string,
) => {
	const edges = getStoreEdges(store, cloudId, projectIdOrKey);
	if (!edges) return [];

	return edges.map((edge) => {
		const node = edge?.getLinkedRecord('node');
		const tabId = node?.getValue('appId');

		return {
			itemType: String(node?.getValue('typeKey')).toLowerCase(),
			itemId: tabId ? String(tabId) : undefined,
			// relay will have already updated the changed item, exclude the new default to restore the original values
			defaultTab: Boolean(node?.getValue('isDefault')),
		};
	});
};
